import React, { useEffect, useRef, useState } from 'react';
import { useHunterFarmerList } from '@/api/auth/useHunterFarmerList';
import { useIsMobile } from '@/hooks';
import { Button } from '@/ui/button';
import { Filter as FilterIcon } from 'lucide-react';

const Filters = ({ onFilterChange }) => {
  const isMobile = useIsMobile();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedChurnLikelihood, setSelectedChurnLikelihood] = useState([]);
  const [selectedRelationship, setSelectedRelationship] = useState([]);
  const [selectedProfileName, setSelectedProfileName] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState([]);

  const { farmerListAPI } = useHunterFarmerList();

  // Keep a stable ref to onFilterChange so it never triggers the useEffect
  const onFilterChangeRef = useRef(onFilterChange);
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  });

  const churnLikelihoodOptions = [
    { id: 1, label: 'Activated' },
    { id: 2, label: 'Dormant' },
    { id: 3, label: 'Sabik' },
    { id: 4, label: 'Sirius' },
    { id: 5, label: 'Spica' },
    { id: 6, label: 'Sargas' },
  ];

  const productsOptions = [
    { id: 1, label: 'Remittance' },
    { id: 2, label: 'Insurance' },
    { id: 3, label: 'Re-KYC' },
    { id: 4, label: 'Referral' },
    { id: 5, label: 'Financing' },
  ];

  const relationshipOptions = [
    { id: 1, label: 'Cold' },
    { id: 2, label: 'Warm' },
    { id: 3, label: 'Hot' },
  ];

  const profileNameOptions = [
    { id: 1, label: 'Core Customer (Loyal)' },
    { id: 2, label: 'Farmable (Avg.)' },
    { id: 3, label: 'Growth Potential' },
    { id: 4, label: 'Need-Based (Avg.)' },
    { id: 5, label: 'Farmable (Tight)' },
    { id: 6, label: 'Farmable (Loose)' },
    { id: 7, label: 'Newbie' },
    { id: 8, label: 'Fresh Meat' },
    { id: 9, label: 'Bargain Hunter' },
    { id: 10, label: 'Need-Based (Tight)' },
    { id: 11, label: 'Need-Based (Loose)' },
    { id: 12, label: 'Core Customer (EX-GF)' },
    { id: 13, label: 'Losers' },
    { id: 14, label: 'Dormant' },
    { id: 15, label: 'Wanderer' },
  ];

  const handleToggleProduct = (optionId) => {
    setSelectedProduct((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const handleToggleRelationship = (optionId) => {
    setSelectedRelationship((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const handleToggleChurnLikelihood = (optionId) => {
    setSelectedChurnLikelihood((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const handleToggleProfileName = (optionId) => {
    setSelectedProfileName((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const handleToggleFarmerList = (optionId) => {
    setSelectedFarmer((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId],
    );
  };

  const Dropdown = ({
    label,
    selected,
    options = [],
    handleToggle,
    onClear,
    scroll = false,
    searchable = false,
  }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = searchable
      ? options.filter((option) => {
          const text = (option.label || option.username || '').toLowerCase();
          return text.includes(searchTerm.toLowerCase());
        })
      : options;

    return (
      <div className="relative group">
        <button
          className="px-4 py-2 bg-transparent hover:bg-yellow-100 rounded-lg
                     flex items-center space-x-2 font-medium text-gray-700
                     transition-colors duration-200"
        >
          <span>{label}</span>
          {selected.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 
                            bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
              {selected.length}
            </span>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>

        <div
          className={`absolute left-0 mt-2 w-56 bg-white/80 backdrop-blur-sm border border-gray-200 
                      rounded-lg shadow-lg py-2 z-50 invisible group-hover:visible
                      transition-all duration-200 opacity-0 group-hover:opacity-100
                      ${scroll ? 'max-h-80 overflow-y-auto' : ''}`}
        >
          {searchable && (
            <div className="px-3 pb-2">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 border rounded-md text-sm
                           focus:outline-none focus:ring-1 focus:ring-yellow-400"
              />
            </div>
          )}

          {filteredOptions.map((option) => {
            const optionId = option.id ?? option.user_id;
            return (
              <label
                key={optionId}
                className="flex items-center px-4 py-2 hover:bg-yellow-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(optionId)}
                  onChange={() => handleToggle(optionId)}
                  className="h-4 w-4 rounded border-gray-300 text-yellow-500 
                             focus:ring-yellow-500 cursor-pointer"
                />
                <span className="ml-3 text-gray-700">{option.label || option.username}</span>
              </label>
            );
          })}

          {selected.length > 0 && (
            <div className="border-t border-gray-200 mt-2 pt-2 px-4">
              <button onClick={onClear} className="text-sm text-yellow-600 hover:text-yellow-700">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Live filter — fires on every selection change, but onFilterChange is
  // accessed via ref so it never itself triggers this effect
  useEffect(() => {
    onFilterChangeRef.current({
      churnlikelihood: selectedChurnLikelihood,
      products: selectedProduct,
      relationship: selectedRelationship,
      profilename: selectedProfileName,
      farmer: selectedFarmer,
    });
  }, [selectedChurnLikelihood, selectedProduct, selectedRelationship, selectedProfileName, selectedFarmer]);

  // Count total active filters
  const activeFilterCount = selectedChurnLikelihood.length + selectedProduct.length +
                            selectedRelationship.length + selectedProfileName.length +
                            selectedFarmer.length;

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedChurnLikelihood([]);
    setSelectedProduct([]);
    setSelectedRelationship([]);
    setSelectedProfileName([]);
    setSelectedFarmer([]);
  };

  // Mobile Filter Drawer
  if (isMobile) {
    return (
      <>
        <Button
          onClick={() => setIsFilterOpen(true)}
          className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
        >
          <FilterIcon className="w-4 h-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 
                            bg-yellow-400 text-black text-xs font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </Button>

        {isFilterOpen && (
          <div className="fixed inset-0 z-[100] bg-white flex flex-col">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-bold">Filters</h2>
              <button onClick={() => setIsFilterOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
              <div>
                <h3 className="font-bold text-base mb-3">Churn Likelihood</h3>
                <div className="space-y-2">
                  {churnLikelihoodOptions.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={selectedChurnLikelihood.includes(option.id)} onChange={() => handleToggleChurnLikelihood(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-base mb-3">Product</h3>
                <div className="space-y-2">
                  {productsOptions.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={selectedProduct.includes(option.id)} onChange={() => handleToggleProduct(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-base mb-3">Relationship</h3>
                <div className="space-y-2">
                  {relationshipOptions.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={selectedRelationship.includes(option.id)} onChange={() => handleToggleRelationship(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-base mb-3">Profile Name</h3>
                <div className="space-y-2">
                  {profileNameOptions.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={selectedProfileName.includes(option.id)} onChange={() => handleToggleProfileName(option.id)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-bold text-base mb-3">Owner</h3>
                <div className="mb-2">
                  <input type="text" placeholder="Search owner..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(farmerListAPI || []).map((option) => {
                    const optionId = option.id ?? option.user_id;
                    return (
                      <label key={optionId} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input type="checkbox" checked={selectedFarmer.includes(optionId)} onChange={() => handleToggleFarmerList(optionId)} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500" />
                        <span className="ml-3 text-gray-900">{option.label || option.username}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-2">
              <Button onClick={clearAllFilters} variant="outline" className="flex-1">
                Clear All
              </Button>
              <Button
                onClick={() => setIsFilterOpen(false)}
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop Filter Layout
  return (
    <div>
      <h1 className="font-bold pb-4">Quick Filter</h1>
      <div className="flex flex-row justify-between items-center">
        <p className="font-bold text-gray-500">Filter by:</p>

        <Dropdown
          label="Churn Likelihood"
          selected={selectedChurnLikelihood}
          options={churnLikelihoodOptions}
          handleToggle={handleToggleChurnLikelihood}
          onClear={() => setSelectedChurnLikelihood([])}
        />

        <Dropdown
          label="Product"
          selected={selectedProduct}
          options={productsOptions}
          handleToggle={handleToggleProduct}
          onClear={() => setSelectedProduct([])}
        />

        <Dropdown
          label="Relationship"
          selected={selectedRelationship}
          options={relationshipOptions}
          handleToggle={handleToggleRelationship}
          onClear={() => setSelectedRelationship([])}
        />

        <Dropdown
          label="Profile Name"
          selected={selectedProfileName}
          options={profileNameOptions}
          handleToggle={handleToggleProfileName}
          onClear={() => setSelectedProfileName([])}
          scroll
        />

        <Dropdown
          label="Owner"
          selected={selectedFarmer}
          options={farmerListAPI || []}
          handleToggle={handleToggleFarmerList}
          onClear={() => setSelectedFarmer([])}
          scroll
          searchable
        />

        <button
          onClick={clearAllFilters}
          className="px-4 py-2 bg-transparent hover:bg-yellow-100 rounded-lg
                     flex items-center space-x-2 font-bold text-gray-700
                     underline transition-colors duration-200"
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
};

export default Filters;